import { useQuery } from '@apollo/client';

import { makeStyles } from 'tss-react/mui';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { EventLog, EventLogVariables } from './Queries/_QueryTypes/EventLog';
import query from './Queries/EventLogQuery.gql';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDTable from '../../../../../../../BaseComponents/Table/EDTable';
import EDTableHead from '../../../../../../../BaseComponents/Table/Components/EDTableHead';
import EDTableRow from '../../../../../../../BaseComponents/Table/Components/EDTableRow';
import EDTableCell from '../../../../../../../BaseComponents/Table/Components/EDTableCell';
import EDTableBody from '../../../../../../../BaseComponents/Table/Components/EDTableBody';
import Typography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { EDCard } from '../../../../../../../BaseComponents/Card/EDCard';
import { EDCardHeader } from '../../../../../../../BaseComponents/Card/EDCardHeader';
import { EDCardContent } from '../../../../../../../BaseComponents/Card/EDCardContent';
import { useIsTouchDevice } from '../../../../../../../Components/BreakpointContext/BreakpointContextUtils';

interface IProps {
  open: boolean;
  onClose: () => void;
  fakturaID: string | undefined;
}

const useStyles = makeStyles()(theme => ({
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  cardRowTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginRight: theme.spacing(0.5),
  },
  loaderContainer: {
    position: 'relative',
    height: '60px',
    marginTop: theme.spacing(2),
  },
  noMargin: {
    margin: '0',
  },
  centerLoader: {
    position: 'absolute',
    right: '50%',
    top: '50%',
    transform: 'translate(-50%,-50%)',
  },
  tableRowLoaderContainer: {
    position: 'relative',
    height: '60px',
  },
}));

const EventLogModal: React.FC<React.PropsWithChildren<IProps>> = props => {
  const isTouchDevice = useIsTouchDevice();
  const { data, loading } = useQuery<EventLog, EventLogVariables>(query, {
    variables: { nodeID: props.fakturaID ?? '' },
    fetchPolicy: 'cache-and-network',
    skip: !props.fakturaID,
    notifyOnNetworkStatusChange: true,
  });

  const { classes, cx } = useStyles();

  const formatEventDescription = (desc: string | null | undefined) => {
    if (!desc) {
      return '';
    }

    return desc.split('||').map((str, index) => {
      return (
        <Typography
          key={index}
          className={cx({
            [classes.noMargin]: isTouchDevice,
          })}
        >
          {str}
        </Typography>
      );
    });
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      fullHeight
      maxWidth={false}
      title="Fakturans händelselogg"
      footerActions={[
        {
          label: 'Stäng',
          onClick: props.onClose,
        },
      ]}
    >
      {isTouchDevice ? (
        data ? (
          <div className={classes.cardContainer}>
            {loading && (
              <div className={classes.loaderContainer}>
                <EDAvenyProgress />
              </div>
            )}

            {data?.loggaLRFaktura?.map((event, index) => {
              return (
                <EDCard key={index}>
                  <EDCardHeader title={event?.eventTime} />
                  <EDCardContent>
                    <Typography>
                      <span className={classes.cardRowTitle}>Begrepp:</span>
                      {event?.classLabel}
                    </Typography>
                    <Typography>
                      <span className={classes.cardRowTitle}>Händelse:</span>
                      {event?.eventType}
                    </Typography>

                    <Typography>
                      <span className={classes.cardRowTitle}>Användare:</span>
                      {event?.user}
                    </Typography>
                    <Typography>
                      <span className={classes.cardRowTitle}>Detaljer:</span>
                      {formatEventDescription(event?.description)}
                    </Typography>
                  </EDCardContent>
                </EDCard>
              );
            })}
          </div>
        ) : (
          <EDAvenyProgress />
        )
      ) : (
        <EDTable>
          {data ? (
            <>
              <EDTableHead>
                <EDTableRow>
                  <EDTableCell>
                    <Typography bold>Tidpunkt</Typography>
                  </EDTableCell>
                  <EDTableCell>
                    <Typography bold>Händelse</Typography>
                  </EDTableCell>
                  <EDTableCell>
                    <Typography bold>Begrepp</Typography>
                  </EDTableCell>
                  <EDTableCell>
                    <Typography bold>Användare</Typography>
                  </EDTableCell>
                  <EDTableCell>
                    <Typography bold>Detaljer</Typography>
                  </EDTableCell>
                </EDTableRow>
              </EDTableHead>
              <EDTableBody>
                {loading && (
                  <EDTableRow className={cx(classes.tableRowLoaderContainer)}>
                    <div
                      className={
                        (classes.loaderContainer, classes.centerLoader)
                      }
                    >
                      <EDAvenyProgress />
                    </div>
                  </EDTableRow>
                )}

                {data?.loggaLRFaktura?.map((event, index) => {
                  return (
                    <EDTableRow key={index}>
                      <EDTableCell>
                        <Typography>{event?.eventTime}</Typography>
                      </EDTableCell>
                      <EDTableCell>
                        <Typography>{event?.eventType}</Typography>
                      </EDTableCell>
                      <EDTableCell>
                        <Typography>{event?.classLabel}</Typography>
                      </EDTableCell>
                      <EDTableCell>
                        <Typography>{event?.user}</Typography>
                      </EDTableCell>
                      <EDTableCell>
                        <Typography>
                          {formatEventDescription(event?.description)}
                        </Typography>
                      </EDTableCell>
                    </EDTableRow>
                  );
                })}
              </EDTableBody>
            </>
          ) : (
            <EDAvenyProgress />
          )}
        </EDTable>
      )}
    </Dialog>
  );
};

export default EventLogModal;
